import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { Comment } from '../../lib/api/comments';
import { Pagination } from '../../lib/api/Pagination';
import PaginatedEntries from '../PaginatedEntries';
import deleteItemFromObj from '../../lib/utils';
import { CommunityPostInfo } from '../../lib/api/musicKnowledgeHub';

export interface State {
  comments: Record<number, Comment>;
  postComments: Record<number, PaginatedEntries>;
  feedbackComments: Record<number, Comment[]>;
  commentsRepliesIds: Record<number, number[]>;
  isFetchingComments: boolean;
  isFetchingById: boolean;
  isAddingComment: boolean;
  loadingCommentLikeId?: number;
  loadingCommentRemoveLikeId?: number;
  isAddingCommentReply: boolean;
  errorFetchComments?: string;
  errorAddComment?: string;
  errorAddCommentReply?: string;
  errorLikeComment?: string;
  errorRemoveLikeComment?: string;
  errorFetchById?: string;
  blogPostInfoData: CommunityPostInfo;
  totalCommentsCountData: number;
}

export const initialState: State = {
  comments: {},
  postComments: {},
  feedbackComments: {},
  commentsRepliesIds: {},
  isFetchingComments: false,
  isAddingComment: false,
  isAddingCommentReply: false,
  isFetchingById: false,
  blogPostInfoData: {
    id: 0,
    title: '',
    slug: '',
    body: '',
    community: {
      id: 0,
      name: '',
      slug: '',
      accept_demos: false,
      accept_all_genres: false,
      genres: [],
      label_started_on: '',
      posts: '',
      color: '',
      total_discussion: 0,
    },
    created_at: 0,
    votes: 0,
    logged_in_user_voted: 0,
    total_comments: 0,
  },
  totalCommentsCountData: 0,
};

export default createSlice({
  name: 'MUSICKNOWLEDGEHUB_COMMENTS',
  initialState,
  reducers: {
    setBlogPostInformationData: (
      state,
      action: PayloadAction<CommunityPostInfo>,
    ) => {
      state.blogPostInfoData = action.payload;
    },
    setTotalCommentsCountData: (state, action: PayloadAction<number>) => {
      state.totalCommentsCountData = action.payload;
    },
    addCommentData: (state, action: PayloadAction<Comment[]>) => {
      const comments = action.payload;

      // put all comments and all their replies into state.comments
      comments.forEach((comment) => {
        state.comments[comment.id] = comment;

        // add all replies to state.comments
        comment.replies.forEach((reply) => {
          state.comments[reply.id] = reply;
        });

        // add all replies ids to state.commentsRepliesIds
        state.commentsRepliesIds[comment.id] = comment.replies.map(
          (reply) => reply.id,
        );
      });
    },

    requestPostComments: (state) => {
      state.errorFetchComments = undefined;
      state.isFetchingComments = true;
    },
    // TODO: RECHECK
    receivePostComments: (
      state,
      action: PayloadAction<{
        comments: Comment[];
        postId: number;
        pagination: Pagination;
      }>,
    ) => {
      const { postId, comments, pagination } = action.payload;

      if (state.postComments[postId] === undefined) {
        state.postComments[postId] = { data: {} };
      }

      state.postComments[postId].data[pagination.currentPage] = comments.map(
        (comment) => comment.id,
      );
      state.postComments[postId].pagination = pagination;
      state.isFetchingComments = false;
    },

    errorPostComments: (state, action: PayloadAction<string>) => {
      state.errorFetchComments = action.payload;
      state.isFetchingComments = false;
    },

    addPostCommentRequest: (state) => {
      state.errorAddComment = undefined;
      state.isAddingComment = true;
    },
    // TODO: RECHECK
    addPostCommentSuccess: (
      state,
      action: PayloadAction<{ comment: Comment; postId: number }>,
    ) => {
      const { postId, comment } = action.payload;

      if (!state.postComments[postId]) {
        state.postComments[postId] = {
          data: {
            1: [comment.id],
          },
        };
      } else {
        state.postComments[postId].data[1].unshift(comment.id);
      }

      state.isAddingComment = false;
    },
    errorAddPostComment: (state, action: PayloadAction<string>) => {
      state.errorAddComment = action.payload;
      state.isAddingComment = false;
    },

    addCommentReplyRequest: (state) => {
      state.errorAddCommentReply = undefined;
      state.isAddingCommentReply = true;
    },
    errorAddCommentReply: (state, action: PayloadAction<string>) => {
      state.errorAddCommentReply = action.payload;
      state.isAddingCommentReply = false;
    },

    requestCommentLike: (state, action: PayloadAction<number>) => {
      state.loadingCommentLikeId = action.payload;
      state.errorLikeComment = undefined;
    },
    successCommentLike: (state, action: PayloadAction<number>) => {
      const commentId = action.payload;
      state.comments[commentId].user_liked_comment = true;
      state.comments[commentId].like_count += 1;

      state.loadingCommentLikeId = undefined;
    },
    errorCommentLike: (state, action: PayloadAction<string>) => {
      state.errorLikeComment = action.payload;
      state.loadingCommentLikeId = undefined;
    },

    requestCommentRemoveLike: (state, action: PayloadAction<number>) => {
      state.loadingCommentRemoveLikeId = action.payload;
      state.errorRemoveLikeComment = undefined;
    },
    successCommentRemoveLike: (state, action: PayloadAction<number>) => {
      const commentId = action.payload;
      state.comments[commentId].user_liked_comment = false;
      state.comments[commentId].like_count -= 1;

      state.loadingCommentRemoveLikeId = undefined;
    },
    errorCommentRemoveLike: (state, action: PayloadAction<string>) => {
      state.errorRemoveLikeComment = action.payload;
      state.loadingCommentRemoveLikeId = undefined;
    },

    // TODO: RECHECK
    successCommentDelete: (
      state,
      action: PayloadAction<{
        commentId: number;
        parentCommentId?: number;
        postId?: number;
      }>,
    ) => {
      const { commentId, parentCommentId, postId } = action.payload;
      let comments: number[] = [];

      // we're dealing with reply
      if (parentCommentId) {
        comments = state.commentsRepliesIds[parentCommentId];
        const index = comments.indexOf(commentId);
        comments.splice(index, 1);
        // we're dealing with a comment
      } else if (postId) {
        state.postComments[postId].data = deleteItemFromObj(
          state.postComments[postId].data,
          commentId,
        );
      }
    },
    fetchCommentById: (state) => {
      state.isFetchingById = true;
      state.errorFetchById = undefined;
    },
    errorFetchCommentById: (
      state,
      { payload: message }: PayloadAction<string>,
    ) => {
      state.isFetchingById = false;
      state.errorFetchById = message;
    },
  },
});
